import React, { useEffect, useState } from 'react';
import { BlockStack, Box, Text } from '@shopify/polaris';
import { Card } from '@/components/card/card';
import {
  Rocket,
  ListChecks,
  BarChart3,
  Bell,
  Settings as SettingsIcon,
  CreditCard,
  HelpCircle,
  Book,
  Package,
} from 'lucide-react';

/**
 * Icon mapping for categories
 */
const CATEGORY_ICONS = {
  'getting-started': Rocket,
  subscriptions: ListChecks,
  notifications: Bell,
  analytics: BarChart3,
  preorders: Package,
  settings: SettingsIcon,
  billing: CreditCard,
  troubleshooting: HelpCircle,
  default: Book,
};

/**
 * Help Sidebar Component
 *
 * Fixed-width navigation (280px) with lucide icons and gradient effects.
 *
 * Why .hc-* classes instead of CSS modules: single source of truth in
 * resources/css/help-center.scss shared with Blade SSR.
 *
 * Location: resources/react/src/modules/help/components/help-sidebar.jsx
 *
 * @component
 *
 * @param {Object} props
 * @param {Array} props.navigation - Navigation structure from API
 * @param {string} [props.activeCategory] - Currently active category
 * @param {string} [props.activeSlug] - Currently active article slug
 * @param {Function} props.onArticleSelect - Callback when article is clicked
 */
export const HelpSidebar = ({
  navigation,
  activeCategory,
  activeSlug,
  onArticleSelect,
}) => {
  const [expandedCategories, setExpandedCategories] = useState([]);

  // Auto-expand active category on mount
  useEffect(() => {
    if (activeCategory && !expandedCategories.includes(activeCategory)) {
      setExpandedCategories((prev) => [...prev, activeCategory]);
    }
  }, [activeCategory, expandedCategories]);

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const getCategoryIcon = (categoryId) => {
    const IconComponent = CATEGORY_ICONS[categoryId] || CATEGORY_ICONS.default;
    return <IconComponent size={20} />;
  };

  if (!navigation || navigation.length === 0) {
    return (
      <Card>
        <Box padding="400">
          <Text tone="subdued" alignment="center">
            No documentation available
          </Text>
        </Box>
      </Card>
    );
  }

  return (
    <Card>
      <BlockStack gap="0">
        {navigation.map((category, index) => {
          const isExpanded = expandedCategories.includes(category.id);
          const isActiveCategory = activeCategory === category.id;
          const hasArticles = category.articles && category.articles.length > 0;

          return (
            <div
              key={category.id}
              className={`hc-sidebar__category ${index === 0 ? 'hc-sidebar__category--first' : ''}`}
            >
              {/* Category Header */}
              <button
                type="button"
                className={`hc-sidebar__category-header ${
                  isActiveCategory ? 'hc-sidebar__category-header--active' : ''
                }`}
                onClick={() => toggleCategory(category.id)}
              >
                {/* Why plain HTML instead of Polaris InlineStack: InlineStack
                   defaults to flex-wrap: wrap which breaks long category names
                   (e.g. "Managing Subscriptions" pushes chevron to new line).
                   The .hc-sidebar__category-header already has flex layout. */}
                <span className="hc-sidebar__category-label">
                  <span className="hc-sidebar__category-icon">
                    {getCategoryIcon(category.id)}
                  </span>
                  <span className="hc-sidebar__category-name">
                    {category.title}
                  </span>
                </span>
                {hasArticles && (
                  <span
                    className={`hc-sidebar__chevron ${
                      isExpanded ? 'hc-sidebar__chevron--expanded' : ''
                    }`}
                  >
                    ▶
                  </span>
                )}
              </button>

              {/* Articles List */}
              {isExpanded && hasArticles && (
                <div
                  className="hc-sidebar__articles"
                  style={{
                    animation: 'slideDown 0.2s ease-out',
                  }}
                >
                  {category.articles.map((article) => {
                    const isActive =
                      activeCategory === category.id &&
                      activeSlug === article.slug;

                    return (
                      <button
                        key={article.slug}
                        type="button"
                        className={`hc-sidebar__article ${
                          isActive ? 'hc-sidebar__article--active' : ''
                        }`}
                        onClick={() =>
                          onArticleSelect(category.id, article.slug)
                        }
                      >
                        <Text>{article.title}</Text>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </BlockStack>
    </Card>
  );
};
